import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { CrawlInstruction, MetaTagName, OGType, WebsiteName } from './seo-constants';
import { DOCUMENT } from '@angular/common';
import { SalesArticleDto } from '@app/api';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private _document = inject(DOCUMENT);

  public setTitle(title: string): void {
    this.titleService.setTitle(title);
    this.setOGTitle(title);
  }

  public setDescription(description: string): void {
    this.metaService.updateTag({ name: MetaTagName.Description, content: description });
    this.setOGDescription(description);
  }

  public setOGTitle(title: string): void {
    this.metaService.updateTag({ property: MetaTagName.OGTitle, content: title });
  }

  public setOGType(type: OGType): void {
    this.metaService.updateTag({ property: MetaTagName.OGType, content: type });
  }

  public setOGSiteName(name: string): void {
    this.metaService.updateTag({ property: MetaTagName.OGSiteName, content: name });
  }

  public setOGUrl(url: string): void {
    this.metaService.updateTag({ property: MetaTagName.OGUrl, content: url });
  }

  public setOGDescription(description: string): void {
    this.metaService.updateTag({ property: MetaTagName.OGDescription, content: description });
  }

  public setOGImage(url: string): void {
    this.metaService.updateTag({ property: MetaTagName.OGImage, content: url });
  }

  public setOGProductTags(salesArticle: SalesArticleDto) {
    this.metaService.updateTag({ property: MetaTagName.OGProductBrand, content: salesArticle.brand });
    this.metaService.updateTag({
      property: MetaTagName.OGProductAvailability,
      content: salesArticle.isSoldOut === true ? 'oos' : 'instock'
    });
    this.metaService.updateTag({ property: MetaTagName.OGProductCondition, content: 'new' });
    if (salesArticle.volumeDiscounts) {
      this.metaService.updateTag({
        property: MetaTagName.OGProductPriceAmount,
        content: salesArticle.volumeDiscounts[salesArticle.volumeDiscounts.length - 1].pricePerArticle
          .toFixed(2)
          .toString()
      });
    }

    this.metaService.updateTag({ property: MetaTagName.OGProductPriceCurrency, content: 'EUR' });
    this.metaService.updateTag({ property: MetaTagName.OGProductRetailerItemId, content: salesArticle.id });
  }

  public setRobots(crawlInstruction: CrawlInstruction) {
    this.metaService.updateTag({ name: MetaTagName.Robots, content: crawlInstruction });
  }

  public cleanUpMetaTags() {
    this.titleService.setTitle(WebsiteName);
    for (const tagName in MetaTagName) {
      if (typeof tagName === MetaTagName.toString()) {
        this.removeTag(tagName as MetaTagName);
      }
    }
  }

  private removeTag(tagName: MetaTagName) {
    const selector = `name='${tagName}'`;
    if (this.metaService.getTag(selector) !== undefined) {
      this.metaService.removeTag(selector);
    }
  }
}
